import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";
import { getContractSummary } from "redux/crm/report/contractSlice";
import { formatNumber } from "helpers/utils";

const sumBy = (rows, key) => (rows || []).reduce((acc, r) => acc + Number(r[key] || 0), 0);

const ContractSummary = ({ criteria }) => {
  const dispatch = useDispatch();
  const { summaryData } = useSelector((s) => s.crm_report_contract);
  const { summary_by_region = [], summary_by_handler = [] } = summaryData || {};

  useEffect(() => {
    dispatch(getContractSummary(criteria));
  }, [dispatch, criteria]);

  const year = new Date().getFullYear();

  const totals = {
    contractValue: sumBy(summary_by_region, "contract_value"),
    contractWeight: sumBy(summary_by_region, "contract_weight"),
    addendumValue: sumBy(summary_by_region, "addendum_value"),
    addendumWeight: sumBy(summary_by_region, "addendum_weight"),
    countContracts: sumBy(summary_by_handler, "count_of_contracts"),
    countAddendums: sumBy(summary_by_handler, "count_of_addendums")
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-4">
      <div className="tw-text-center">
        <h4 className="tw-text-lg tw-font-bold tw-text-gray-900 tw-mb-1">
          Summary list <span className="tw-text-primary-600">{year} (up to date)</span>
        </h4>
        <p className="tw-text-sm tw-text-slate-500 tw-mb-0">
          Contracts &amp; addendums signed, rolled up by region and handler (filtered by contract date, released only).
        </p>
      </div>

      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-4">
        {/* Region roll-up */}
        <div className="lg:tw-col-span-2 tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-overflow-hidden">
          <div className="tw-px-4 tw-py-3 tw-border-b tw-border-slate-100">
            <h5 className="tw-text-sm tw-font-semibold tw-text-primary-700 tw-mb-0">Contract &amp; addendum signed by region</h5>
          </div>
          <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-text-sm tw-text-slate-700">
              <thead>
                <tr className="tw-bg-slate-50 tw-text-slate-500 tw-text-xs tw-uppercase">
                  <th rowSpan={2} className="tw-text-left tw-px-4 tw-py-2 tw-align-middle">Region</th>
                  <th colSpan={2} className="tw-text-center tw-px-4 tw-py-2 tw-border-l tw-border-slate-100">Total contract</th>
                  <th colSpan={2} className="tw-text-center tw-px-4 tw-py-2 tw-border-l tw-border-slate-100">Total addendum</th>
                </tr>
                <tr className="tw-bg-slate-50 tw-text-slate-500 tw-text-xs">
                  <th className="tw-text-right tw-px-4 tw-py-2 tw-border-l tw-border-slate-100">Value (USD)</th>
                  <th className="tw-text-right tw-px-4 tw-py-2">Weight (M.T)</th>
                  <th className="tw-text-right tw-px-4 tw-py-2 tw-border-l tw-border-slate-100">Value (USD)</th>
                  <th className="tw-text-right tw-px-4 tw-py-2">Weight (M.T)</th>
                </tr>
              </thead>
              <tbody className="tw-divide-y tw-divide-slate-100">
                {summary_by_region.map((r, i) => (
                  <tr key={i} className="hover:tw-bg-slate-50">
                    <td className="tw-px-4 tw-py-2">{r.name || "Other"}</td>
                    <td className="tw-px-4 tw-py-2 tw-text-right tw-text-primary-700 tw-tabular-nums">${formatNumber(r.contract_value)}</td>
                    <td className="tw-px-4 tw-py-2 tw-text-right tw-tabular-nums">{formatNumber(r.contract_weight)}</td>
                    <td className="tw-px-4 tw-py-2 tw-text-right tw-text-cyan-700 tw-tabular-nums">${formatNumber(r.addendum_value)}</td>
                    <td className="tw-px-4 tw-py-2 tw-text-right tw-tabular-nums">{formatNumber(r.addendum_weight)}</td>
                  </tr>
                ))}
                {summary_by_region.length === 0 && (
                  <tr><td colSpan={5} className="tw-px-4 tw-py-6 tw-text-center tw-text-slate-400">No data</td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="tw-bg-slate-100 tw-font-bold tw-text-slate-800">
                  <td className="tw-px-4 tw-py-2">Total</td>
                  <td className="tw-px-4 tw-py-2 tw-text-right tw-text-primary-700 tw-tabular-nums">${formatNumber(totals.contractValue)}</td>
                  <td className="tw-px-4 tw-py-2 tw-text-right tw-tabular-nums">{formatNumber(totals.contractWeight)}</td>
                  <td className="tw-px-4 tw-py-2 tw-text-right tw-text-cyan-700 tw-tabular-nums">${formatNumber(totals.addendumValue)}</td>
                  <td className="tw-px-4 tw-py-2 tw-text-right tw-tabular-nums">{formatNumber(totals.addendumWeight)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Handler roll-up */}
        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-slate-200 tw-shadow-sm tw-overflow-hidden">
          <div className="tw-px-4 tw-py-3 tw-border-b tw-border-slate-100">
            <h5 className="tw-text-sm tw-font-semibold tw-text-primary-700 tw-mb-0">Handle summary</h5>
          </div>
          <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-text-sm tw-text-slate-700">
              <thead>
                <tr className="tw-bg-slate-50 tw-text-slate-500 tw-text-xs tw-uppercase">
                  <th className="tw-text-left tw-px-4 tw-py-2">Handled by</th>
                  <th className="tw-text-right tw-px-4 tw-py-2">Contracts</th>
                  <th className="tw-text-right tw-px-4 tw-py-2">Addendums</th>
                </tr>
              </thead>
              <tbody className="tw-divide-y tw-divide-slate-100">
                {summary_by_handler.map((r, i) => (
                  <tr key={i} className="hover:tw-bg-slate-50">
                    <td className="tw-px-4 tw-py-2">{r.name}</td>
                    <td className="tw-px-4 tw-py-2 tw-text-right tw-tabular-nums">{r.count_of_contracts}</td>
                    <td className="tw-px-4 tw-py-2 tw-text-right tw-tabular-nums">{r.count_of_addendums}</td>
                  </tr>
                ))}
                {summary_by_handler.length === 0 && (
                  <tr><td colSpan={3} className="tw-px-4 tw-py-6 tw-text-center tw-text-slate-400">No data</td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="tw-bg-slate-100 tw-font-bold tw-text-slate-800">
                  <td className="tw-px-4 tw-py-2">Total</td>
                  <td className="tw-px-4 tw-py-2 tw-text-right tw-tabular-nums">{totals.countContracts}</td>
                  <td className="tw-px-4 tw-py-2 tw-text-right tw-tabular-nums">{totals.countAddendums}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

ContractSummary.propTypes = { criteria: PropTypes.object };

export default ContractSummary;
